import { useCallback, useEffect, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Button } from '@/components/ui/button'
import {
  getProductionDeliveryAdminFn,
  reviewProductionDeliverableFn,
} from '@/lib/firebase/functions'
import { formatUserFacingError } from '@/lib/errors'
import {
  requirementDeadline,
  type ParticipantRequirement,
  type Deliverable,
} from '@/types/productionRequirements'

export function DeliverableReviewDialog({
  submissionId,
  onClose,
  onSaved,
}: {
  submissionId: string | null
  onClose: () => void
  onSaved: () => void
}) {
  const [data, setData] = useState<{
    requirements: ParticipantRequirement[]
    version: number
    deliverables: Record<string, Deliverable>
  } | null>(null)
  const [error, setError] = useState('')
  const refresh = useCallback(async () => {
    if (!submissionId) return
    try {
      setData((await getProductionDeliveryAdminFn({ submissionId })).data)
    } catch (e) {
      setError(formatUserFacingError(e, 'Could not load materials.'))
    }
  }, [submissionId])
  useEffect(() => {
    setData(null)
    setError('')
    void refresh()
  }, [refresh])
  return (
    <Dialog
      open={!!submissionId}
      onOpenChange={(open) => {
        if (!open) onClose()
      }}
    >
      <DialogContent className="max-h-[85vh] max-w-2xl overflow-auto">
        <DialogHeader>
          <DialogTitle>Review participant materials</DialogTitle>
          <DialogDescription>
            Download and check the complete file before verifying. Approval applies to the displayed
            revision.
          </DialogDescription>
        </DialogHeader>
        {error && <p role="alert">{error}</p>}
        {!data && !error && <p>Loading…</p>}
        {data?.requirements.map((r) => (
          <ReviewItem
            key={`${r.id}-${data.deliverables[r.id]?.revision ?? 0}`}
            requirement={r}
            delivery={data.deliverables[r.id]}
            currentVersion={data.version}
            submissionId={submissionId!}
            onSaved={() => {
              void refresh()
              onSaved()
            }}
          />
        ))}
      </DialogContent>
    </Dialog>
  )
}

function ReviewItem({
  requirement,
  delivery,
  currentVersion,
  submissionId,
  onSaved,
}: {
  requirement: ParticipantRequirement
  delivery?: Deliverable
  currentVersion: number
  submissionId: string
  onSaved: () => void
}) {
  const [note, setNote] = useState(delivery?.reviewNote ?? '')
  const [archiveLocation, setArchiveLocation] = useState(delivery?.archiveLocation ?? '')
  const [checked, setChecked] = useState(false)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const current = delivery?.requirementVersion === currentVersion
  async function review(status: 'verified' | 'needs_changes') {
    if (!delivery) return
    setBusy(true)
    setError('')
    try {
      await reviewProductionDeliverableFn({
        submissionId,
        requirementId: requirement.id,
        revision: delivery.revision,
        status,
        note,
        archiveLocation,
      })
      onSaved()
    } catch (e) {
      setError(formatUserFacingError(e, 'Could not save review.'))
    } finally {
      setBusy(false)
    }
  }
  return (
    <section className="space-y-3 rounded border p-4">
      <h3 className="font-medium">{requirement.label}</h3>
      <p className="text-sm">Due {requirementDeadline(requirement)}</p>
      <p className="text-sm">
        {delivery
          ? `Revision ${delivery.revision} · ${current ? delivery.status.replace(/_/g, ' ') : 'Requirements changed; resubmission needed'}`
          : 'Awaiting submission'}
      </p>
      {delivery && (
        <>
          <a
            href={delivery.url}
            target="_blank"
            rel="noopener noreferrer"
            className="break-all text-sm underline"
          >
            Open download link
          </a>
          {delivery.accessNote && (
            <p className="whitespace-pre-wrap text-sm">
              Access instructions: {delivery.accessNote}
            </p>
          )}
          <Label htmlFor={`review-note-${requirement.id}`}>Review or correction note</Label>
          <Textarea
            id={`review-note-${requirement.id}`}
            value={note}
            onChange={(e) => setNote(e.target.value)}
          />
          <Label htmlFor={`review-copy-${requirement.id}`}>
            Where the downloaded production copy is stored
          </Label>
          <Input
            id={`review-copy-${requirement.id}`}
            value={archiveLocation}
            onChange={(e) => setArchiveLocation(e.target.value)}
          />
          <label className="flex items-start gap-2 text-sm">
            <input
              type="checkbox"
              checked={checked}
              onChange={(e) => setChecked(e.target.checked)}
            />
            I downloaded the complete file and checked its identity, playback, and audio.
          </label>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <div className="flex flex-wrap gap-2">
            <Button
              disabled={busy || !current || !checked || !archiveLocation.trim()}
              onClick={() => void review('verified')}
            >
              Verify this revision
            </Button>
            <Button
              variant="outline"
              disabled={busy || !current || !note.trim()}
              onClick={() => void review('needs_changes')}
            >
              Request correction
            </Button>
          </div>
        </>
      )}
    </section>
  )
}
